/**
 * src/data/projects.js
 *
 * Structured portfolio data migrated out of src/components/Work.js.
 * `media[].key` values match keys in public/media/manifest.json
 * (produced by scripts/optimize-assets.js), except zippo's svg cover which
 * points straight at its raw path.
 */

export const PROJECTS = [
  {
    slug: 'nighthouse',
    title: 'Nighthouse — Nightlife & Social App',
    client: 'Bluprise',
    category: 'Own product',
    tier: 'hero',
    year: null,
    brief: 'A nightlife and social app that turns the city after dark into a live map of real tables, live audio rooms, and people nearby.',
    result: 'Shipped as a full in-browser web app — auth, live map, rooms with polls and raise-your-hand, ratings, trips, and profiles — live at /projects/nighthouse/.',
    stack: ['React', 'Realtime UX', 'Auth & Accounts', 'Maps'],
    media: [
      { key: 'nighthouse/nighthouse-home', alt: 'Nighthouse home screen with live map' },
      { key: 'nighthouse/nighthouse-features', alt: 'Nighthouse feature overview screen' },
    ],
    liveUrl: 'https://nighthouse.app',
    staticPage: '/projects/nighthouse/',
    phone: false,
    accent: '#E8B44C',
  },
  {
    slug: 'wardany-dev',
    title: 'Wardany Strategic Development — Real Estate Developer Site',
    client: 'Wardany Strategic Development',
    category: 'Brand site',
    tier: 'hero',
    year: 2026,
    brief: 'A bilingual (English and Arabic) brand site for an Egyptian real estate developer, built around a live contour-map shader, an interactive WebGPU 3D build sequence from foundation to finished compound, and sharp-rendered flipbook brochures.',
    result: 'Live at wardany.dev with a loading screen, an X-ray view of plumbing, power and air, a cost-at-each-stage calculator, an admin with lead inbox, mailbox, content editor and colour switcher, and its own mail server.',
    stack: ['Three.js WebGPU', 'GSAP', 'GLSL shader', 'Bilingual RTL', 'Admin CMS', 'Mail server'],
    media: [{ key: 'wardany/wardany-home', alt: 'Wardany Strategic Development home page with the contour shader' }],
    liveUrl: 'https://www.wardany.dev',
    staticPage: null,
    phone: false,
    accent: '#A25C32',
  },
  {
    slug: 'laya-n',
    title: 'Layan — Ideas, Made Live',
    client: 'Bluprise',
    category: 'Own product',
    tier: 'hero',
    year: 2026,
    brief: 'An AI operating layer that turns business intent into strategy, systems, software and agents, while keeping humans in control of every consequential decision.',
    result: 'Live at laya-n.com with a four-stage operating loop, agents that work in the open, subscription-plus-credits pricing, and a mailbox on its own domain.',
    stack: ['AI agents', 'Node', 'Realtime UX', 'Subscriptions'],
    media: [{ key: 'laya-n/laya-n-home', alt: 'Layan home page' }],
    liveUrl: 'https://laya-n.com',
    staticPage: null,
    phone: false,
    accent: '#6D5BD0',
  },
  {
    slug: 'flion',
    title: 'FLION LLC — Private Investment House',
    client: 'FLION LLC',
    category: 'Brand site',
    tier: 'hero',
    year: null,
    brief: 'A cinematic, dark-luxe brand experience for a private investment house spanning strategic investments, commodity trading, and executive advisory.',
    result: 'Delivered a Next.js brand site with 3D/WebGL visuals, GSAP-driven scroll, and a client portal, live at /projects/flion/.',
    stack: ['Next.js', '3D/WebGL', 'GSAP', 'Brand Identity'],
    media: [
      { key: 'flion/flion-home', alt: 'FLION LLC homepage hero' },
    ],
    liveUrl: null,
    staticPage: '/projects/flion/',
    phone: false,
    accent: '#b8912f',
  },
  {
    slug: 'snowcane',
    title: 'SNOW® Snowcane — Premium Sugarcane Beverage',
    client: 'SNOW®',
    category: 'Brand site',
    tier: 'hero',
    year: null,
    brief: 'A cinematic single-page brand experience for a premium sugarcane beverage, built around a scroll-driven can that re-tints per flavor.',
    result: 'Hand-built with gold-foil typography, animated cane fields, a testimonial carousel, and a dark/bright theme toggle, with zero dependencies for instant load.',
    stack: ['Vanilla JS', 'SVG Illustration', 'Scroll Animation'],
    media: [
      { key: 'snowcane/snowcane-home', alt: 'Snowcane homepage hero' },
      { key: 'snowcane/snowcane-flavors', alt: 'Snowcane flavors section' },
      { key: 'snowcane/snowcane-story', alt: 'Snowcane brand story section' },
    ],
    liveUrl: null,
    staticPage: '/projects/snowcane/',
    phone: false,
    accent: '#B4903F',
  },
  {
    slug: 'snowsbeauty',
    title: 'Snowsbeauty — Skincare & Beauty Brand',
    client: 'Snow Beauty',
    category: 'E-commerce',
    tier: 'hero',
    year: null,
    brief: 'A live Shopify storefront for a premium makeup brand built around a pink-winter-dream world of floating products and crystal animations.',
    result: 'Wired to full Shopify commerce — catalog, cart, and checkout — with a scroll-to-enter hero, live at snowsbeauty.myshopify.com.',
    stack: ['Shopify', 'E-commerce Storefront', 'Email Capture'],
    media: [
      { key: 'snowsbeauty/snowsbeauty-home', alt: 'Snowsbeauty Shopify storefront homepage' },
    ],
    liveUrl: 'https://snowsbeauty.myshopify.com',
    staticPage: null,
    phone: false,
    accent: '#b07a8a',
  },
  {
    slug: 'pnw-floor-design',
    title: 'PNW Floor Design — Flooring, Tile & Interior Design',
    client: 'PNW Floor Design LLC',
    category: 'Brand site',
    tier: 'hero',
    year: 2026,
    brief: 'A multi-page site for a Washington flooring and interior design studio: services, areas, gallery, guides and enquiry, built to rank for design-intent searches.',
    result: 'Live at pnwdesign.bluprise.com with a hub-and-spoke content structure, local SEO pages, and an SEO audit that drives the 90-day plan.',
    stack: ['Static site', 'Local SEO', 'Content hub', 'Enquiry form'],
    media: [{ key: 'pnw/pnw-home', alt: 'PNW Floor Design home page' }],
    liveUrl: 'https://pnwdesign.bluprise.com',
    staticPage: null,
    phone: false,
    accent: '#B4903F',
  },
  {
    slug: 'postify',
    title: 'Postify — AI Social Media Management',
    client: 'Bluprise',
    category: 'Own product',
    tier: 'hero',
    year: 2026,
    brief: 'Brief a campaign, approve the drafts, and let it write, schedule and post a month of content to Meta, YouTube, TikTok, X and LinkedIn from one calendar.',
    result: 'Live at postify.social with AI content, calendar scheduling, analytics and multi-platform publishing.',
    stack: ['React', 'Node', 'AI content', 'Scheduling', 'Analytics'],
    media: [{ key: 'postify/postify-home', alt: 'Postify home page with the content calendar' }],
    liveUrl: 'https://postify.social',
    staticPage: null,
    phone: false,
    accent: '#7C3AED',
  },
  {
    slug: 'fillai',
    title: 'FillAI — AI Browser Copilot',
    client: 'Bluprise',
    category: 'Own product',
    tier: 'archive',
    year: 2026,
    brief: 'A Chrome copilot that fills forms, applies to jobs, scrapes data and schedules automations on the tabs you already have open, with no CLI or cloud VM.',
    result: 'Live at fillai.postify.social with a Chrome extension, a desktop pre-release, and EGP pricing for Egyptian users.',
    stack: ['Chrome extension', 'Electron', 'AI agents', 'Automation'],
    media: [{ key: 'fillai/fillai-home', alt: 'FillAI home page' }],
    liveUrl: 'https://fillai.postify.social',
    staticPage: null,
    phone: false,
    accent: '#22C55E',
  },
  {
    slug: 'dhib',
    title: 'DHIB — Restaurant & Gaming App',
    client: 'DHIB',
    category: 'Web app',
    tier: 'archive',
    year: 2025,
    brief: 'A custom restaurant and gaming application that combines dining experiences with interactive entertainment.',
    result: 'Delivered a multi-section web app covering menu, games, locations, and cart flows.',
    stack: ['Web Development', 'Gaming Integration', 'Payment Systems'],
    media: [
      { key: 'dhib/dhib-vercel-app', alt: 'DHIB home screen' },
      { key: 'dhib/dhib-vercel-app-menu', alt: 'DHIB menu screen' },
      { key: 'dhib/dhib-vercel-app-games', alt: 'DHIB games screen' },
      { key: 'dhib/dhib-vercel-app-locations', alt: 'DHIB locations screen' },
      { key: 'dhib/dhib-vercel-app-cart', alt: 'DHIB cart screen' },
    ],
    liveUrl: null,
    staticPage: null,
    phone: false,
    accent: '#6d28d9',
  },
  {
    slug: 'dialcove',
    title: 'Dialcove — Communication Platform',
    client: 'Dialcove',
    category: 'SaaS',
    tier: 'archive',
    year: 2025,
    brief: 'A modern business communication SaaS platform meant to streamline team collaboration.',
    result: 'Delivered marketing pages covering pricing, resources, about, and jobs alongside the core product site.',
    stack: ['SaaS', 'API Integration', 'Cloud Solutions'],
    media: [
      { key: 'dialcove/dialcove', alt: 'Dialcove homepage' },
      { key: 'dialcove/dialcove-pricing', alt: 'Dialcove pricing page' },
      { key: 'dialcove/dialcove-resources', alt: 'Dialcove resources page' },
      { key: 'dialcove/dialcove-about', alt: 'Dialcove about page' },
      { key: 'dialcove/dialcove-jobs', alt: 'Dialcove jobs page' },
    ],
    liveUrl: null,
    staticPage: null,
    phone: false,
    accent: '#2563eb',
  },
  {
    slug: 'barons-key',
    title: "Baron's Key Voyage — Travel Platform",
    client: "Baron's Key Voyage",
    category: 'Web app',
    tier: 'archive',
    year: 2025,
    brief: 'A luxury travel booking experience for discerning travelers seeking premium accommodations and services.',
    result: 'Delivered a booking platform with luxury branding and payment processing built in.',
    stack: ['Booking Systems', 'Payment Processing', 'Mobile Apps'],
    media: [
      { key: 'barons-key/barons-key-voyage-vercel-app', alt: "Baron's Key Voyage homepage" },
    ],
    liveUrl: null,
    staticPage: null,
    phone: false,
    accent: '#5bc0be',
  },
  {
    slug: 'skyline-housing',
    title: 'Skyline Housing — Property Management',
    client: 'Skyline Housing',
    category: 'Platform',
    tier: 'archive',
    year: null,
    brief: 'A WordPress-based real estate solution for property managers and real estate professionals.',
    result: 'Delivered a CMS-backed property management site with client portals and database-driven listings.',
    stack: ['WordPress', 'CMS', 'Database Design'],
    media: [
      { key: 'skyline-housing/skylinehousing-wordpress', alt: 'Skyline Housing WordPress site' },
    ],
    liveUrl: null,
    staticPage: null,
    phone: false,
    accent: '#10b981',
  },
  {
    slug: 'blubeds',
    title: 'BluBeds — Travel Tech & Hotel Booking',
    client: 'BluBeds',
    category: 'Own product',
    tier: 'hero',
    year: null,
    brief: 'Bluprise’s own travel tech platform for hotel bookings, event stays and business travel.',
    result: 'Delivered a booking platform covering UI/UX, mobile flows, and payment integration for property owners.',
    stack: ['Booking Systems', 'UI/UX Design', 'Payment Integration'],
    media: [
      { key: 'blubeds/blubeds', alt: 'BluBeds homepage' },
      { key: 'blubeds/blubeds1', alt: 'BluBeds booking flow' },
    ],
    liveUrl: 'https://blubeds.com',
    staticPage: null,
    phone: false,
    accent: '#8b5cf6',
  },
  {
    slug: 'hurghada',
    title: 'Hurghada Experience Builder',
    client: 'Hurghada Experience Builder',
    category: 'Web app',
    tier: 'archive',
    year: 2026,
    brief: 'A tourism and experience-booking platform connecting travelers with hotels and events in the Red Sea region.',
    result: 'Delivered a multi-section experience builder covering hotels, events, and El Gouna destination pages.',
    stack: ['Location Services', 'Hotel Booking', 'Event Management'],
    media: [
      { key: 'hurghada/hurghada-experience-builder-vercel-app', alt: 'Hurghada Experience Builder homepage' },
      { key: 'hurghada/hurghada-experience-builder-vercel-app-hotels', alt: 'Hurghada hotels page' },
      { key: 'hurghada/hurghada-experience-builder-vercel-app-events', alt: 'Hurghada events page' },
      { key: 'hurghada/hurghada-experience-builder-vercel-app-el-gouna', alt: 'Hurghada El Gouna page' },
    ],
    liveUrl: null,
    staticPage: null,
    phone: false,
    accent: '#f59e0b',
  },
  {
    slug: 'bizhotels',
    title: 'BizHotels — Trade Show Hotel Booking',
    client: 'BizHotels',
    category: 'Web app',
    tier: 'hero',
    year: null,
    brief: 'A hotel booking platform purpose-built for business travelers attending trade shows and conventions.',
    result: 'Delivered real-time hotel discovery near event venues, corporate rate negotiation, and 3D particle animations in a black/orange UI.',
    stack: ['Next.js', '3D WebGL', 'Hotel Booking'],
    media: [
      { key: 'bizhotels/bizhotels-trade-shows', alt: 'BizHotels trade shows page' },
      { key: 'bizhotels/bizhotels-tradeshows', alt: 'BizHotels trade show listings' },
      { key: 'bizhotels/bizhotels-hotels', alt: 'BizHotels hotel listings page' },
      { key: 'bizhotels/bizhotels-about', alt: 'BizHotels about page' },
      { key: 'bizhotels/bizhotels-contact', alt: 'BizHotels contact page' },
    ],
    liveUrl: null,
    staticPage: null,
    phone: false,
    accent: '#c2410c',
  },
  {
    slug: 'blucab',
    title: 'Blu.Cab — Subscription Rides in Cairo',
    client: 'Blu.Cab',
    category: 'Mobile app',
    tier: 'hero',
    year: null,
    brief: 'A subscription ride platform for Cairo families and professionals, covering school runs, work commutes, and family trips.',
    result: 'Delivered 4 subscription plans with dedicated drivers, live seat monitoring, real-time GPS tracking via Mapbox, child safety features, and an admin dashboard.',
    stack: ['React + Vite', 'Mapbox', 'Shadcn UI', 'Admin Dashboard'],
    media: [
      { key: 'blucab/blucab1', alt: 'Blu.Cab app screen 1' },
      { key: 'blucab/blucab2', alt: 'Blu.Cab app screen 2' },
    ],
    liveUrl: null,
    staticPage: null,
    phone: true,
    accent: '#1d4ed8',
  },
  {
    slug: 'egypt-escape',
    title: 'Egypt Escape Bookings',
    client: 'Egypt Escape Bookings',
    category: 'Web app',
    tier: 'archive',
    year: null,
    brief: "A curated travel discovery platform for Egypt, helping tourists find and book the best escapes from Red Sea resorts to Nile valley hideaways.",
    result: 'Delivered a nearby-attractions map, filterable hotel listings, and a booking flow built with React + Vite and Shadcn UI.',
    stack: ['React + Vite', 'Shadcn UI', 'Map Integration'],
    media: [
      { key: 'egypt-escape/egyptebooking-home', alt: 'Egypt Escape Bookings homepage' },
      { key: 'egypt-escape/egyptebooking-full', alt: 'Egypt Escape Bookings full listing view' },
      { key: 'egypt-escape/egyptebooking-nearby', alt: 'Egypt Escape Bookings nearby attractions map' },
    ],
    liveUrl: null,
    staticPage: null,
    phone: false,
    accent: '#c2410c',
  },
  {
    slug: 'validmyemail',
    title: 'ValidMyEmail — Email Verification SaaS',
    client: 'Bluprise',
    category: 'Own product',
    tier: 'hero',
    year: null,
    brief: 'A bulk email verification SaaS platform that validates mailing lists in real time, catching invalid addresses, spam traps, and disposable emails.',
    result: 'Shipped with a web dashboard, REST API, and MongoDB-backed analytics, deployed on Vercel with a Python verification engine.',
    stack: ['Python', 'MongoDB', 'REST API', 'Vercel'],
    media: [
      { key: 'validmyemail/validmyemail-home', alt: 'ValidMyEmail homepage' },
      { key: 'validmyemail/validmyemail-dashboard', alt: 'ValidMyEmail dashboard' },
    ],
    liveUrl: 'https://validmy.email',
    staticPage: null,
    phone: false,
    accent: '#0891b2',
  },
  {
    slug: 'saleshaven',
    title: 'SalesHaven — AI Telesales Platform',
    client: 'SalesHaven',
    category: 'SaaS',
    tier: 'archive',
    year: null,
    brief: 'An AI-powered telesales platform with real-time voice interaction, dynamic script generation, and CRM integration.',
    result: 'Delivered a web dashboard letting sales teams personalize pitches, handle objections, and log call outcomes automatically.',
    stack: ['Python + Node.js', 'AI Voice Integration', 'CRM'],
    media: [
      { key: 'saleshaven/saleshaven-home', alt: 'SalesHaven homepage' },
    ],
    liveUrl: null,
    staticPage: null,
    phone: false,
    accent: '#7c3aed',
  },
  {
    slug: 'blubedssuite',
    title: 'BluBedsSuite — Tradeshow Hotel Booking',
    client: 'BluBedsSuite',
    category: 'Mobile app',
    tier: 'archive',
    year: null,
    brief: 'A mobile-first platform for managing tradeshow participation, from browsing events to booking hotels near venues.',
    result: 'Delivered team coordination, expense tracking, and event management with a fixed bottom nav and infinite-scroll events feed.',
    stack: ['React', 'Shadcn UI', 'TanStack Query'],
    media: [
      { key: 'blubeds/blubeds', alt: 'BluBedsSuite app home' },
      { key: 'blubeds/blubeds1', alt: 'BluBedsSuite booking screen' },
    ],
    liveUrl: null,
    staticPage: null,
    phone: true,
    accent: '#6d28d9',
  },
  {
    slug: 'zippo',
    title: 'Zippo — Cyberpunk Code Explorer',
    client: 'Zippo',
    category: 'Desktop app',
    tier: 'archive',
    year: null,
    brief: 'A cyberpunk-themed desktop app for browsing GitHub repositories, exploring local project hierarchies, and comparing branches visually.',
    result: 'Delivered diff viewing, file exploration, and GitHub API access via Octokit in an Electron + React desktop shell.',
    stack: ['Electron', 'React', 'Octokit API'],
    media: [
      {
        key: 'zippo/zippo-cover',
        alt: 'Zippo cyberpunk code explorer cover art',
        svg: true,
        src: '/media/zippo/zippo-cover.svg',
      },
    ],
    liveUrl: null,
    staticPage: null,
    phone: false,
    accent: '#1f2937',
  },
];

const HERO_ORDER = [
  'blubeds',
  'nighthouse',
  'wardany-dev',
  'laya-n',
  'pnw-floor-design',
  'flion',
  'snowcane',
  'postify',
  'snowsbeauty',
  'bizhotels',
  'blucab',
  'validmyemail',
];

export const HERO_PROJECTS = HERO_ORDER
  .map((slug) => PROJECTS.find((p) => p.slug === slug))
  .filter(Boolean);

export function getProject(slug) {
  return PROJECTS.find((p) => p.slug === slug) || null;
}
